// Klub panel — Testreszabás (Figma OP_06, 06b, 06c, 06d): főkép, események, posztok.
// Főkép: clubs/{id}.coverUrl (Storage: posts/{uid}/…, a mobilapp útvonala).
// Események: clubs/{id}.events tömb; „Verseny” típusnál valódi tournaments dokumentum is készül.
// Posztok: posts kollekció clubId-vel (a mobilapp klub-posztjainak sémája).
import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  addDoc, collection, deleteDoc, doc, limit, onSnapshot, query, serverTimestamp, Timestamp, updateDoc, where,
} from 'firebase/firestore';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { db, storage } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { Icon } from '../icons';
import { fmtInt, timeAgo, toDate } from '../data';
import { Badge, Button, Card, ConfirmModal, Empty, Field, Input, Modal, PageHeader, SectionTitle, Select, Spinner, Tabs, TextArea, Tone, useToast } from '../ui';
import { ClubInfo, useClub } from './clubData';

type Tab = 'cover' | 'events' | 'posts';

async function uploadImage(uid: string, file: File): Promise<string> {
  const ext = (file.name.split('.').pop() || 'jpg').toLowerCase();
  const r = ref(storage, `posts/${uid}/${Date.now()}.${ext}`);
  await uploadBytes(r, file, { contentType: file.type });
  return getDownloadURL(r);
}

export default function ClubCustomizePage() {
  const { club } = useClub();
  const [params, setParams] = useSearchParams();
  const tab = (params.get('tab') as Tab) || 'cover';
  if (!club) return <Spinner />;
  return (
    <div>
      <PageHeader title="Testreszabás" sub="Klubod megjelenése a Dartiqon appban" />
      <Tabs<Tab> value={tab} onChange={(t) => setParams({ tab: t })} options={[
        { value: 'cover', label: 'Főkép' }, { value: 'events', label: 'Események' }, { value: 'posts', label: 'Posztok' },
      ]} />
      {tab === 'cover' && <CoverTab club={club} />}
      {tab === 'events' && <EventsTab club={club} startNew={params.get('new') === '1'} />}
      {tab === 'posts' && <PostsTab club={club} />}
    </div>
  );
}

/* ---------- Főkép (OP_06) ---------- */
function CoverTab({ club }: { club: ClubInfo }) {
  const { user } = useAuth();
  const toast = useToast();
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const pick = async (f: File | undefined) => {
    if (!f || !user) return;
    if (!f.type.startsWith('image/') || f.size > 5 * 1024 * 1024) { toast('Csak 5 MB alatti kép tölthető fel.', 'red'); return; }
    setBusy(true);
    try { await updateDoc(doc(db, 'clubs', club.id), { coverUrl: await uploadImage(user.uid, f) }); toast('Főkép frissítve.'); } catch { toast('A feltöltés nem sikerült.', 'red'); }
    setBusy(false);
  };
  return (
    <div className="flex flex-col gap-4">
      <Card className="!p-5">
        <SectionTitle>Klub főképe</SectionTitle>
        <div className="aspect-[3/1] max-h-[320px] w-full rounded-xl bg-[#1d232a] border border-[#31383D] overflow-hidden flex items-center justify-center">
          {club.coverUrl ? <img src={club.coverUrl} alt="" className="w-full h-full object-cover" /> : (
            <div className="flex flex-col items-center gap-2 text-[#8a8f99]"><Icon name="ImageSquare" size={30} /><span className="text-[11px]">Ajánlott méret: 1200 × 400 px</span></div>
          )}
        </div>
        <input ref={input} type="file" accept="image/*" className="hidden" onChange={(e) => pick(e.target.files?.[0])} />
        <div className="flex gap-3 mt-4">
          <Button icon="UploadSimple" disabled={busy} onClick={() => input.current?.click()}>{busy ? 'Feltöltés…' : 'Új főkép feltöltése'}</Button>
          {club.coverUrl && (
            <button type="button" onClick={async () => { await updateDoc(doc(db, 'clubs', club.id), { coverUrl: null }); toast('Főkép eltávolítva.'); }}
              className="inline-flex items-center gap-2 h-[37px] px-4 rounded-full border border-[#ef4444] text-[#ef4444] text-[14px] font-semibold hover:bg-[#ef4444]/10">
              <Icon name="Trash" size={16} />Eltávolítás
            </button>
          )}
        </div>
      </Card>
      <Card className="!p-5">
        <SectionTitle>Előnézet az appban</SectionTitle>
        <p className="text-[13px] text-[#B8B8C3]">A főkép a klub közösségi profiljának fejlécében és a Klubok listában jelenik meg.</p>
      </Card>
    </div>
  );
}

/* ---------- Események (OP_06b + 06c) ---------- */
type EvType = 'tournament' | 'special' | 'promo';
interface ClubEvent {
  id: string; title: string; description: string; type: EvType; start: Date | null; end: Date | null;
  tournamentId?: string | null; meta?: Record<string, unknown>;
}
const EV_LABEL: Record<EvType, string> = { tournament: 'Verseny', special: 'Különleges esemény', promo: 'Promóció' };
const EV_TONE: Record<EvType, Tone> = { tournament: 'gold', special: 'purple', promo: 'green' };

const toLocal = (d: Date | null) => (d ? new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16) : '');
const fmtRange = (a: Date | null, b: Date | null) => {
  if (!a) return '';
  const day = `${a.getFullYear()}.${String(a.getMonth() + 1).padStart(2, '0')}.${String(a.getDate()).padStart(2, '0')}.`;
  const t = (d: Date) => `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  return b ? `${day} ${t(a)} – ${t(b)}` : `${day} ${t(a)}`;
};

function EventsTab({ club, startNew }: { club: ClubInfo; startNew: boolean }) {
  const toast = useToast();
  const events: ClubEvent[] = ((club.raw.events as Record<string, unknown>[]) ?? []).map((e) => ({
    id: String(e.id), title: String(e.title ?? ''), description: String(e.description ?? ''), type: (e.type as EvType) ?? 'special',
    start: toDate(e.start), end: toDate(e.end), tournamentId: (e.tournamentId as string) ?? null, meta: (e.meta as Record<string, unknown>) ?? {},
  })).sort((a, b) => (a.start?.getTime() ?? 0) - (b.start?.getTime() ?? 0));
  const [edit, setEdit] = useState<ClubEvent | 'new' | null>(startNew ? 'new' : null);
  const [del, setDel] = useState<ClubEvent | null>(null);

  const saveAll = (list: ClubEvent[]) => updateDoc(doc(db, 'clubs', club.id), {
    events: list.map((e) => ({ ...e, start: e.start ? Timestamp.fromDate(e.start) : null, end: e.end ? Timestamp.fromDate(e.end) : null })),
  });

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-[15px] font-extrabold italic uppercase text-white">Események</h2>
        <Button icon="Plus" onClick={() => setEdit('new')}>Új esemény</Button>
      </div>
      {events.length === 0 ? <Card><Empty>Még nincs esemény.</Empty></Card> : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {events.map((e) => {
            const m = e.meta ?? {};
            const details = e.type === 'tournament'
              ? [m.maxParticipants ? `${m.maxParticipants} fő` : '', m.entryFee ? `nevezés ${fmtInt(Number(m.entryFee))} Ft` : '', m.prizePool ? `nyereményalap ${fmtInt(Number(m.prizePool))} Ft` : ''].filter(Boolean).join(' · ')
              : e.description;
            return (
              <Card key={e.id} pad={false} className="overflow-hidden">
                <div className="h-[110px] bg-[#1d232a]" />
                <div className="p-4">
                  <Badge tone={EV_TONE[e.type]}>{EV_LABEL[e.type]}</Badge>
                  <div className="mt-2 text-[14px] font-semibold text-white">{e.title}</div>
                  <div className="text-[12px] text-[#8a8f99] mt-1">{fmtRange(e.start, e.end)}</div>
                  {details && <div className="text-[12px] text-[#B8B8C3] mt-1 line-clamp-2">{details}</div>}
                  <div className="flex gap-2 mt-3">
                    <Button kind="ghost" icon="PencilSimpleLine" className="!h-[32px] !text-[13px]" onClick={() => setEdit(e)}>Szerkesztés</Button>
                    <button type="button" onClick={() => setDel(e)} className="inline-flex items-center gap-1.5 h-[32px] px-3 rounded-full border border-[#ef4444] text-[#ef4444] text-[13px] font-semibold hover:bg-[#ef4444]/10">
                      <Icon name="Trash" size={14} />Törlés
                    </button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
      {edit && <EventModal club={club} ev={edit === 'new' ? null : edit} onClose={() => setEdit(null)}
        onSave={async (e) => { await saveAll(edit === 'new' ? [...events, e] : events.map((x) => (x.id === e.id ? e : x))); toast('Esemény mentve.'); }} />}
      {del && (
        <ConfirmModal title="Esemény törlése" danger confirmLabel="Törlés" onClose={() => setDel(null)}
          text={<>Biztosan törlöd: <b className="text-white">{del.title}</b>?{del.tournamentId ? ' A kapcsolódó verseny lemondott állapotba kerül.' : ''}</>}
          onConfirm={async () => {
            try {
              await saveAll(events.filter((x) => x.id !== del.id));
              if (del.tournamentId) await updateDoc(doc(db, 'tournaments', del.tournamentId), { status: 'cancelled' }).catch(() => undefined);
              toast('Esemény törölve.');
            } catch { toast('A törlés nem sikerült.', 'red'); }
            setDel(null);
          }} />
      )}
    </div>
  );
}

function EventModal({ club, ev, onClose, onSave }: { club: ClubInfo; ev: ClubEvent | null; onClose: () => void; onSave: (e: ClubEvent) => Promise<void> }) {
  const { user } = useAuth();
  const toast = useToast();
  const m = (ev?.meta ?? {}) as Record<string, string | number>;
  const [title, setTitle] = useState(ev?.title ?? '');
  const [desc, setDesc] = useState(ev?.description ?? '');
  const [type, setType] = useState<EvType>(ev?.type ?? 'tournament');
  const [start, setStart] = useState(toLocal(ev?.start ?? null));
  const [end, setEnd] = useState(toLocal(ev?.end ?? null));
  const [t, setT] = useState({
    format: String(m.format ?? 'Egyszerű kieséses'), gameMode: String(m.gameMode ?? '501'), maxParticipants: String(m.maxParticipants ?? '16'),
    entryFee: String(m.entryFee ?? ''), prizePool: String(m.prizePool ?? ''), regDeadline: String(m.regDeadline ?? ''),
    p1: String(m.p1 ?? ''), p2: String(m.p2 ?? ''), p3: String(m.p3 ?? ''),
  });
  const [busy, setBusy] = useState(false);
  const num = (v: string) => Number(v.replace(/[^\d]/g, '')) || 0;
  const setTv = (k: keyof typeof t, v: string) => setT((x) => ({ ...x, [k]: v }));

  const save = async () => {
    if (!title.trim() || !start) { toast('A cím és a kezdő dátum kötelező.', 'red'); return; }
    setBusy(true);
    try {
      const s = new Date(start);
      const e = end ? new Date(end) : null;
      const meta = type === 'tournament' ? {
        format: t.format, gameMode: t.gameMode, maxParticipants: num(t.maxParticipants), entryFee: num(t.entryFee), prizePool: num(t.prizePool),
        regDeadline: t.regDeadline, p1: num(t.p1), p2: num(t.p2), p3: num(t.p3),
      } : {};
      let tournamentId = ev?.tournamentId ?? null;
      if (type === 'tournament') {
        const tData = {
          name: title.trim(), description: desc.trim(), type: 'offline', status: 'upcoming', startDate: Timestamp.fromDate(s),
          endDate: e ? Timestamp.fromDate(e) : null, maxParticipants: num(t.maxParticipants), gameMode: t.gameMode, isPublic: true,
          clubId: club.id, venueName: club.name, format: t.format, entryFee: num(t.entryFee),
          prizePool: { total: num(t.prizePool), first: num(t.p1), second: num(t.p2), third: num(t.p3) },
        };
        if (tournamentId) await updateDoc(doc(db, 'tournaments', tournamentId), tData);
        else tournamentId = (await addDoc(collection(db, 'tournaments'), { ...tData, participantIds: [], createdBy: user?.uid ?? 'club', createdAt: serverTimestamp() })).id;
      }
      await onSave({ id: ev?.id ?? `ev_${Date.now()}`, title: title.trim(), description: desc.trim(), type, start: s, end: e, tournamentId, meta });
      onClose();
    } catch (err) {
      console.warn('[club] esemény', err);
      toast('A mentés nem sikerült.', 'red');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={ev ? 'Esemény szerkesztése' : 'Új esemény'} onClose={onClose} width={640}
      footer={<><Button kind="ghost" onClick={onClose}>Mégse</Button><Button disabled={busy} onClick={save}>Mentés</Button></>}>
      <div className="flex flex-col gap-4">
        <Field label="Cím *"><Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="pl. Péntek esti 501 kupa" /></Field>
        <Field label="Leírás"><TextArea rows={2} value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Havi klubverseny kezdőknek és haladóknak..." /></Field>
        <Field label="Típus *">
          <Select<EvType> value={type} onChange={setType} options={[
            { value: 'tournament', label: 'Verseny (létrehoz egy valódi versenyt is)' }, { value: 'special', label: 'Különleges esemény' }, { value: 'promo', label: 'Promóció' },
          ]} />
        </Field>
        {type === 'tournament' && (
          <div className="p-4 rounded-xl border border-[#d9a514]/60" style={{ background: 'rgba(217,165,20,0.04)' }}>
            <h4 className="text-[14px] font-extrabold italic uppercase text-white mb-3">Verseny beállítások</h4>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Verseny típus"><Select value={t.format} onChange={(v) => setTv('format', v)} options={['Egyszerű kieséses', 'Dupla kieséses', 'Körmérkőzés', 'Csoportkör + kiesés'].map((x) => ({ value: x, label: x }))} /></Field>
              <Field label="Játékmód"><Select value={t.gameMode} onChange={(v) => setTv('gameMode', v)} options={['501', '301', '701', 'Cricket'].map((x) => ({ value: x, label: x }))} /></Field>
              <Field label="Max résztvevők"><Input inputMode="numeric" value={t.maxParticipants} onChange={(e) => setTv('maxParticipants', e.target.value.replace(/[^\d]/g, ''))} /></Field>
              <Field label="Nevezési díj (Ft)"><Input inputMode="numeric" value={t.entryFee} onChange={(e) => setTv('entryFee', e.target.value.replace(/[^\d]/g, ''))} placeholder="2 000" /></Field>
              <Field label="Nyereményalap (Ft)"><Input inputMode="numeric" value={t.prizePool} onChange={(e) => setTv('prizePool', e.target.value.replace(/[^\d]/g, ''))} placeholder="40 000" /></Field>
              <Field label="Regisztrációs határidő"><Input type="date" value={t.regDeadline} onChange={(e) => setTv('regDeadline', e.target.value)} className="[color-scheme:dark]" /></Field>
              <Field label="1. helyezés"><Input inputMode="numeric" value={t.p1} onChange={(e) => setTv('p1', e.target.value.replace(/[^\d]/g, ''))} placeholder="20 000 Ft" /></Field>
              <Field label="2. helyezés"><Input inputMode="numeric" value={t.p2} onChange={(e) => setTv('p2', e.target.value.replace(/[^\d]/g, ''))} placeholder="12 000 Ft" /></Field>
              <Field label="3. helyezés"><Input inputMode="numeric" value={t.p3} onChange={(e) => setTv('p3', e.target.value.replace(/[^\d]/g, ''))} placeholder="8 000 Ft" /></Field>
            </div>
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Kezdő dátum *"><Input type="datetime-local" value={start} onChange={(e) => setStart(e.target.value)} className="[color-scheme:dark]" /></Field>
          <Field label="Befejező dátum"><Input type="datetime-local" value={end} onChange={(e) => setEnd(e.target.value)} className="[color-scheme:dark]" /></Field>
        </div>
      </div>
    </Modal>
  );
}

/* ---------- Posztok (OP_06d) ---------- */
interface Post { id: string; title: string; content: string; imageUrl: string | null; at: Date | null; likes: number; comments: number }

function PostsTab({ club }: { club: ClubInfo }) {
  const toast = useToast();
  const [posts, setPosts] = useState<Post[] | null>(null);
  const [edit, setEdit] = useState<Post | 'new' | null>(null);
  const [del, setDel] = useState<Post | null>(null);
  useEffect(() => onSnapshot(query(collection(db, 'posts'), where('clubId', '==', club.id), limit(60)), (s) => {
    setPosts(s.docs.map((d) => {
      const v = d.data();
      return {
        id: d.id, title: String(v.title ?? ''), content: String(v.content ?? ''), imageUrl: v.imageUrl ?? null, at: toDate(v.createdAt),
        likes: Array.isArray(v.likes) ? v.likes.length : 0, comments: Number(v.commentCount ?? 0),
      };
    }).sort((a, b) => (b.at?.getTime() ?? 0) - (a.at?.getTime() ?? 0)));
  }, () => setPosts([])), [club.id]);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-[15px] font-extrabold italic uppercase text-white">Posztok</h2>
        <Button icon="Plus" onClick={() => setEdit('new')}>Új poszt</Button>
      </div>
      {posts === null ? <Spinner /> : posts.length === 0 ? <Card><Empty>Még nincs poszt.</Empty></Card> : (
        <div className="flex flex-col gap-3">
          {posts.map((p) => (
            <Card key={p.id} className="!p-4 flex items-center gap-4">
              <div className="w-[112px] h-[76px] rounded-xl bg-[#1d232a] overflow-hidden shrink-0">{p.imageUrl && <img src={p.imageUrl} alt="" className="w-full h-full object-cover" />}</div>
              <div className="flex-1 min-w-0">
                {p.title && <div className="text-[14px] font-semibold text-white truncate">{p.title}</div>}
                <div className={`text-[13px] ${p.title ? 'text-[#B8B8C3]' : 'text-white'} line-clamp-2`}>{p.content}</div>
                <div className="text-[11px] text-[#8a8f99] mt-1">{timeAgo(p.at)} · {p.likes} kedvelés · {p.comments} hozzászólás</div>
              </div>
              <button type="button" onClick={() => setEdit(p)} aria-label="Szerkesztés" className="inline-flex items-center justify-center w-[34px] h-[34px] rounded-[10px] bg-[#1d232a] border border-[#31383D] text-[#B8B8C3] hover:text-white"><Icon name="PencilSimpleLine" size={16} /></button>
              <button type="button" onClick={() => setDel(p)} aria-label="Törlés" className="inline-flex items-center justify-center w-[34px] h-[34px] rounded-[10px] bg-[#1d232a] border border-[#31383D] text-[#ef4444]"><Icon name="Trash" size={16} /></button>
            </Card>
          ))}
        </div>
      )}
      {edit && <PostModal club={club} post={edit === 'new' ? null : edit} onClose={() => setEdit(null)} />}
      {del && (
        <ConfirmModal title="Poszt törlése" danger confirmLabel="Törlés" onClose={() => setDel(null)} text="A poszt eltűnik a klub oldaláról és a hírfolyamból."
          onConfirm={async () => { try { await deleteDoc(doc(db, 'posts', del.id)); toast('Poszt törölve.'); } catch { toast('A törlés nem sikerült.', 'red'); } setDel(null); }} />
      )}
    </div>
  );
}

function PostModal({ club, post, onClose }: { club: ClubInfo; post: Post | null; onClose: () => void }) {
  const { user } = useAuth();
  const toast = useToast();
  const input = useRef<HTMLInputElement>(null);
  const [title, setTitle] = useState(post?.title ?? '');
  const [content, setContent] = useState(post?.content ?? '');
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!content.trim()) { toast('A poszt szövege kötelező.', 'red'); return; }
    setBusy(true);
    try {
      const imageUrl = file && user ? await uploadImage(user.uid, file) : post?.imageUrl ?? null;
      if (post) await updateDoc(doc(db, 'posts', post.id), { title: title.trim(), content: content.trim(), imageUrl, type: imageUrl ? 'image' : 'text' });
      else await addDoc(collection(db, 'posts'), {
        userId: user?.uid ?? club.id, username: club.name, type: imageUrl ? 'image' : 'text', title: title.trim(), content: content.trim(), imageUrl,
        clubId: club.id, clubKind: 'news', featured: false, likes: [], commentCount: 0, createdAt: serverTimestamp(),
      });
      toast('Poszt mentve.');
      onClose();
    } catch {
      toast('A mentés nem sikerült.', 'red');
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal title={post ? 'Poszt szerkesztése' : 'Új poszt'} onClose={onClose} width={560}
      footer={<><Button kind="ghost" onClick={onClose}>Mégse</Button><Button disabled={busy} onClick={save}>{busy ? 'Mentés…' : 'Közzététel'}</Button></>}>
      <div className="flex flex-col gap-4">
        <Field label="Cím (opcionális)"><Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="pl. Darts & Drink Happy Hour" /></Field>
        <Field label="Szöveg *"><TextArea rows={4} value={content} onChange={(e) => setContent(e.target.value)} placeholder="Mit szeretnél megosztani a játékosokkal?" /></Field>
        <input ref={input} type="file" accept="image/*" className="hidden" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
        <Button kind="ghost" icon="ImageSquare" onClick={() => input.current?.click()}>{file ? file.name : post?.imageUrl ? 'Kép cseréje' : 'Kép hozzáadása'}</Button>
      </div>
    </Modal>
  );
}
